import { Component, inject, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { RequestNoteDetailTable } from 'src/app/models/tables/invoicing/request-note-detail-table.model';
import { RequestNoteTableModel } from 'src/app/models/tables/invoicing/request-note-table.model';
import { HeaderService } from 'src/app/services/header/header.service';
import { RequestNoteDetailService } from 'src/app/services/invoicing/request-note-detail.service';
import { RequestNoteService } from 'src/app/services/invoicing/request-note.service';

@Component({
  selector: 'app-request-note',
  templateUrl: './request-note.component.html',
  styleUrls: ['./request-note.component.scss'],
  standalone: false
})
export class RequestNoteComponent implements OnInit {

  private readonly headerService = inject(HeaderService);
  private readonly requestNoteDetailService = inject(RequestNoteDetailService);
  private readonly requestNoteService = inject(RequestNoteService);
  private readonly router = inject(Router);
  private readonly title = inject(Title);

  activeTabIndex = 0;
  ascendant: boolean = true;
  columnSort: string | null = null;
  detailData: PagedResponseModel<RequestNoteDetailTable> = new PagedResponseModel<RequestNoteDetailTable>();
  detailHeader: ColumnResponseModel[] = [];
  filters: ColumnResponseModel[] = [];
  generalData: PagedResponseModel<RequestNoteTableModel> = new PagedResponseModel<RequestNoteTableModel>();
  generalHeader: ColumnResponseModel[] = [];
  isOpened: string | undefined = undefined
  limit: number = 10;
  nameWindow: string = 'Solicitud de Notas';
  page: number = 1;
  previousSort: { field: string; isAscsendant: number };
  projectName = ProjectName.projectName;
  tableActions: TableAction[] = [
    {
      label: 'Editar',
      icon: 'pi pi-pencil',
      action: 'edit'
    },
  ];
  url: string = 'request-note';

  async ngOnInit() {
    this.title.setTitle(`Solicitud de Notas -  ${this.projectName}`);
    this.headerService.setTitle(this.nameWindow);
    const filters = localStorage.getItem(this.url);
    if (filters) this.filters = JSON.parse(filters);
    await Promise.all([
      this.getColumnsForGeneralConsultation(),
      this.getRequestNoteWithPagination(),
    ]);
  }

  async exportDetailConsultation() {
    try {
      const response = await firstValueFrom(this.requestNoteDetailService.exportDetailConsultation(this.filters, this.columnSort, this.ascendant));
      const blob = response;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Solicitud de Notas Detalle.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al exportar las solicitudes de notas detalle.', error);
    }
  }

  async exportGeneralConsultation() {
    try {
      const response = await firstValueFrom(this.requestNoteService.exportGeneralConsultation(this.filters, this.columnSort, this.ascendant));
      const blob = response;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Solicitud de Notas.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al exportar las solicitudes de notas.', error);
    }
  }

  async getAllRequestNoteDetailsWithPagination() {
    try {
      this.detailData = await firstValueFrom(this.requestNoteDetailService.getAllRequestNoteDetailsWithPagination(
        this.page, this.limit, this.columnSort, this.ascendant, this.filters
      ));
    } catch (error) {
      console.error('Error al buscar los detalles de las solicitudes de notas.', error);
    }
  }

  async getColumnsForDetailConsultation() {
    try {
      if (this.detailHeader.length === 0) {
        this.detailHeader = await firstValueFrom(this.requestNoteDetailService.getColumnsForDetailConsultation());
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta detalle.', error);
    }
  }

  async getColumnsForGeneralConsultation() {
    try {
      if (this.generalHeader.length === 0) {
        this.generalHeader = await firstValueFrom(this.requestNoteService.getColumnsForGeneralConsultation());
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta general.', error);
    }
  }

  async getRequestNoteWithPagination() {
    try {
      this.generalData = await firstValueFrom(this.requestNoteService.getRequestNotesWithPagination(this.page, this.limit, this.columnSort, this.ascendant, this.filters));
    } catch (error) {
      console.error('Error al buscar las solicitudes de notas.', error);
    }
  }

  public async handleTabChange(tabIndex: number) {
    this.generalHeader.length = this.generalHeader.length === 1 ? 0 : this.generalHeader.length;
    this.detailHeader.length = this.detailHeader.length === 1 ? 0 : this.detailHeader.length;

    if (this.activeTabIndex === tabIndex) return;

    this.page = 1;
    this.limit = 10;
    this.activeTabIndex = tabIndex;

    switch (this.activeTabIndex) {
      case 0:
        await Promise.all([
          this.getColumnsForGeneralConsultation(),
          this.getRequestNoteWithPagination()
        ]);
        this.generalData = {
          ...this.generalData,
          currentPage: 1,
          pageSize: 10
        };
        break;
      case 1:
        await Promise.all([
          this.getColumnsForDetailConsultation(),
          this.getAllRequestNoteDetailsWithPagination()
        ]);
        this.detailData = {
          ...this.detailData,
          currentPage: 1,
          pageSize: 10
        };
        break;
    }
  }

  newRequestNote() {
    this.router.navigate([`/${this.url}/register`]);
  }

  onAction(event: { action: string, item?: RequestNoteTableModel }) {
    if (event.action === 'edit' && event.item) {
      this.router.navigate([`/${this.url}/register/${event.item.id}`]);
    }
  }

  async onExport() {
    switch (this.activeTabIndex) {
      case 0:
        await this.exportGeneralConsultation();
        break;
      case 1:
        await this.exportDetailConsultation();
        break;
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters
      .map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index)
      .map((item: any) => item.value);
    this.filters = this.removeDuplicates(filters);
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    switch (this.activeTabIndex) {
      case 0:
        await this.getRequestNoteWithPagination()
        break;
      case 1:
        await this.getAllRequestNoteDetailsWithPagination()
        break;
    }
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

}